import React, { useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuIcon from '@mui/icons-material/Menu';
import EngineFileList from './EngineFileList';
import { useFormContext } from 'react-hook-form';
import CodeEditor from '../CodeEditor/CodeEditor';
import { InferenceEngineFileTypes } from '../../enums';
import { ApiHelper } from '../../common/ApiHelper';
import SaveIcon from '@mui/icons-material/Save';
import { Button, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import useAppUtils from '../../Utils/useAppUtils';
import CreateNewFile from './CreateNewFile';
import Loader from '../Loader';
import { PLATFORM_PY_TEXT } from '../../data/app-engine-data';
import Progress from '../Loaders/Progress';

const uploadFileTypes = [
  { name: 'Source File(s)', id: InferenceEngineFileTypes.SourceFile },
  { name: 'Model File(s)', id: InferenceEngineFileTypes.ModelFiles },
];

const CodeEditorContainer = ({ onUpload, onDelete, onCreateNewFile, onSaveFileChanges }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editorValue, setEditorValue] = useState('');
  const [language, setLanguage] = useState('javascript');
  const [uploadMenuAnchorEl, setUploadMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const openUploadFilesMenu = Boolean(uploadMenuAnchorEl);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [selectedUploadFileType, setSelectedUploadFileType] = useState(null);
  const { alert, confirm } = useAppUtils();
  const [showAddNewFileDialog, setShowAddNewFileDialog] = useState(false);
  const [isCodesUpdated, setIsCodesUpdated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [codeEditorReadOnlyEnable, setCodeEditorReadOnlyEnable] = useState(false);

  const {
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const downloadText = async (node) => {
    const engineId = getValues('engineId');
    if (!engineId) {
      return;
    }
    let newtext = '';
    setIsLoading(true);
    try {
      const { data, status } = await ApiHelper.GetDownloadEngineFileLink(engineId, node.type, node.fileName);
      if (status === 200) {
        const response = await fetch(data.publicDownloadLink);
        newtext = '';
        if (response && response.status === 200) {
          newtext = await response.text();
        }
        setIsLoading(false);
        return newtext;
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Unable to download file', { error, node });
      return newtext;
    }
  };
  const onTreeItemLoad = async (node) => {
    // console.log('node', node);
    setSelectedFile(null);
    if (isCodesUpdated) {
      const isOk = await confirm('Do you want to save the changes?');
      if (!isOk) {
        setEditorValue(null);
        setIsCodesUpdated(false);
      } else {
        onSaveFileChanges(selectedFile, editorValue);
        setIsCodesUpdated(false);
      }
    }
    if (
      node &&
      (node.type === InferenceEngineFileTypes.SourceFile || node.type === InferenceEngineFileTypes.PackageDefinitionFile)
    ) {
      setSelectedFile(node);
      const text = await downloadText(node);
      setLanguage('python');
      setEditorValue(text);
      setCodeEditorReadOnlyEnable(false);
    } else if (node && node.type === InferenceEngineFileTypes.ModelFiles) {
      setSelectedFile(node);
    } else if (node && node.type === InferenceEngineFileTypes.PlatformFile) {
      setSelectedFile(node);
      setEditorValue(PLATFORM_PY_TEXT);
      setLanguage('python');
      setCodeEditorReadOnlyEnable(true);
    }
  };

  const onEditorUpdate = async (value) => {
    // console.log('onEditorUpdate', { value, selectedFile });
    setEditorValue(value);
    if (value !== PLATFORM_PY_TEXT) {
      setIsCodesUpdated(true);
    }
  };

  const saveFileChanges = (e) => {
    onSaveFileChanges(selectedFile, editorValue);
    setIsCodesUpdated(false);
  };
  const discardChanges = async () => {
    // console.log('discarded');
    const isOk = await confirm('Are you sure you want to discard the changes?');
    if (isOk) {
      setEditorValue(null);
      setIsCodesUpdated(false);
    }
  };

  const createNewFile = (value) => {
    // console.log('createNewFile', value);
    setShowAddNewFileDialog(false);
    onCreateNewFile(value);
  };

  const handleCloseUploadMenu = () => {
    setUploadMenuAnchorEl(null);
  };
  const onUploadMenuItemClick = (item) => {
    // console.log('item', item, fileInput.current);
    setSelectedUploadFileType(item);
    if (fileInput && fileInput.current) {
      fileInput.current.accept = item.id === InferenceEngineFileTypes.SourceFile ?  '.py' : '';
      fileInput.current.click();
      handleCloseUploadMenu();
    }
  };
  const validateFiles = (files) => {
    const type = selectedUploadFileType?.id;
    const formats = ['.py'];
    if (
      type === InferenceEngineFileTypes.SourceFile &&
      formats &&
      files.some((file) => !formats.some((format) => file.name.toLowerCase().endsWith(format.toLowerCase())))
    ) {
      // console.log(`Only following file formats are acceptable: ${formats.join(', ')}`);
      alert(`Only following file formats are acceptable: ${formats.join(', ')}`, 'warning');
      return false;
    }
    return true;
  };
  const handleFileChange = (event) => {
    const selectedFiles = [...event.target.files];
    const type = selectedUploadFileType?.id;
    // console.log('type', type);
    if (validateFiles(selectedFiles)) {
      onUpload({ type, files: selectedFiles });
      // fileInput.current = null;
    }
  };

  const deleteFile = () => {
    // console.log('selectedFile', selectedFile);
    resetEditor();
    onDelete(selectedFile);
  };
  const onLastItemUpdate = (lastSelectedItem) => {
    // console.log('selectedNode', lastSelectedItem);
    // setSelectedNode(lastSelectedItem);
  };

  const resetEditor = () => {
    setEditorValue(null);
    setIsCodesUpdated(false);
    setSelectedFile(null);
  };

  useEffect(() => {
    // setEditorValue(getValues('requirementFileStr'));
  }, []);
  return (
    <>
      <div className="editor-container">
        <div className="engine-sidebar" style={{ width: isOpen ? '15%' : '4%' }}>
          {isOpen && (
            <div className="toolbar">
              <span title="Create Source File">
                <AddIcon className="icon" titleAccess="Create Source File" onClick={() => setShowAddNewFileDialog(true)} />
              </span>
              <span title="Upload Files">
                {' '}
                <UploadFileIcon
                  className="icon"
                  titleAccess="Upload Files"
                  onClick={(event) => setUploadMenuAnchorEl(event.currentTarget)}
                />
                <input
                  type="file"
                  hidden
                  id="browse"
                  onChange={handleFileChange}
                  ref={fileInput}
                  multiple
                />
              </span>
              <span>
                <DeleteIcon
                  className={
                    selectedFile !== null &&
                    selectedFile.type !== InferenceEngineFileTypes.PackageDefinitionFile &&
                    selectedFile.type !== InferenceEngineFileTypes.PlatformFile &&
                    selectedFile.fileName !== 'EntryPoint.py'
                      ? 'icon'
                      : 'disabled-icon'
                  }
                  titleAccess="Delete File"
                  onClick={(e) => deleteFile()}
                />
              </span>
              {showAddNewFileDialog && (
                <CreateNewFile
                  open={showAddNewFileDialog}
                  onClose={() => setShowAddNewFileDialog(false)}
                  onSubmit={createNewFile}
                  files={getValues('files')}
                />
              )}
            </div>
          )}
          {!isOpen && (
            <div className="toolbar" style={{ justifyContent: 'center', paddingRight: 0, borderBottom: 'none' }}>
              <MenuIcon className="icon" onClick={() => setIsOpen(!isOpen)} />
            </div>
          )}
          {isOpen && (
            <EngineFileList files={getValues('files')} onTreeItemLoad={onTreeItemLoad} onLastItemUpdate={onLastItemUpdate} />
          )}
          <Popper
            style={{
              zIndex: 1,
            }}
            open={openUploadFilesMenu}
            anchorEl={uploadMenuAnchorEl}
            role={undefined}
            transition
            placement="bottom-start"
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                  marginTop: '4px',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleCloseUploadMenu}>
                    <MenuList id="split-button-menu" autoFocusItem>
                      {uploadFileTypes.map((option, index) => (
                        <MenuItem
                          key={option.id}
                          // disabled={index === 2}
                          // selected={option.id === selectedSearchType.id}
                          onClick={(event) => onUploadMenuItemClick(option)}
                        >
                          {option.name}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
        <div className="main-content" style={{ width: isOpen ? '85%' : '96%' }}>
          <div className="toolbar">
            <div className="left">
              {/* <span><DarkModeIcon/></span> */}
              {isOpen && (
                <span>
                  <MenuIcon className="icon" onClick={() => setIsOpen(!isOpen)} />
                </span>
              )}
            </div>
            {isCodesUpdated && (
              <div className="right">
                {/* <div ><span className="icon"><DarkModeIcon/></span></div> */}
                <div>
                  <span className="icon">
                    <SaveIcon titleAccess="Save Changes" onClick={(e) => saveFileChanges(e)} />
                  </span>
                </div>
                <div>
                  <span className="icon">
                    <UndoIcon titleAccess="Discard Changes" onClick={() => discardChanges()} />
                  </span>
                </div>
              </div>
            )}
          </div>
          {isLoading && <Progress progressLabel="Please wait..." />}
          <CodeEditor value={editorValue} onUpdate={onEditorUpdate} language={language} readOnly={codeEditorReadOnlyEnable} selectedFile={selectedFile}/>
        </div>
      </div>
    </>
  );
};

export default CodeEditorContainer;
